import React, { CSSProperties, PropsWithChildren } from 'react';

import { Box, keyframes } from '@mui/material';

type ElectricBorderProps = PropsWithChildren<{
  color?: string;
  className?: string;
  style?: CSSProperties;
  pulsate?: boolean;
  durationMs?: number;
}>;

const ElectricBorder: React.FC<ElectricBorderProps> = ({
  children,
  color = '#5227FF',
  className,
  style,
  pulsate = true,
  durationMs = 1500
}: ElectricBorderProps) => {
  const baseW = 2;
  const blur1 = 1 + baseW * 0.5;
  const blur2 = 2 + baseW;
  const minW = 2;
  const maxW = 6;
  const pulseWidth = keyframes({
    '0%': { borderWidth: `${minW}px` },
    '50%': { borderWidth: `${maxW}px` },
    '100%': { borderWidth: `${minW}px` }
  });
  const glow1MinBlur = 1 + minW * 0.5;
  const glow1MaxBlur = 1 + maxW * 0.5;
  const glow2MinBlur = 2 + minW;
  const glow2MaxBlur = 2 + maxW;
  const pulseGlow1 = keyframes({
    '0%': { filter: `blur(${glow1MinBlur}px)` },
    '50%': { filter: `blur(${glow1MaxBlur}px)` },
    '100%': { filter: `blur(${glow1MinBlur}px)` }
  });
  const pulseGlow2 = keyframes({
    '0%': { filter: `blur(${glow2MinBlur}px)` },
    '50%': { filter: `blur(${glow2MaxBlur}px)` },
    '100%': { filter: `blur(${glow2MinBlur}px)` }
  });

  return (
    <Box
      className={`electric-border ${className ?? ''}`}
      style={style}
      sx={{
        position: 'relative',
        borderRadius: 'inherit',
        overflow: 'visible',
        isolation: 'isolate'
      }}
    >
      <Box
        className='eb-layers'
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          zIndex: 2
        }}
      >
        <Box
          className='eb-stroke'
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            borderStyle: 'solid',
            borderColor: `${color}`,
            borderWidth: `${baseW}px`,
            ...(pulsate && {
              animation: `${pulseWidth} ${durationMs}ms ease-in-out infinite`
            })
          }}
        />
        <Box
          className='eb-glow-1'
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            borderStyle: 'solid',
            borderColor: `oklch(from ${color} l c h / 0.6)`,
            borderWidth: `${baseW}px`,
            opacity: 0.7,
            filter: `blur(${blur1}px)`,
            ...(pulsate && {
              animation: `${pulseWidth} ${durationMs}ms ease-in-out infinite, ${pulseGlow1} ${durationMs}ms ease-in-out infinite`
            })
          }}
        />
        <Box
          className='eb-glow-2'
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            borderStyle: 'solid',
            borderColor: `oklch(from ${color} l c h)`,
            borderWidth: `${baseW}px`,
            opacity: 0.6,
            filter: `blur(${blur2}px)`,
            ...(pulsate && {
              animation: `${pulseWidth} ${durationMs}ms ease-in-out infinite, ${pulseGlow2} ${durationMs}ms ease-in-out infinite`
            })
          }}
        />
      </Box>
      <Box
        className='eb-content'
        sx={{
          position: 'relative',
          borderRadius: 'inherit',
          zIndex: 1
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ElectricBorder;
